import { motion } from "framer-motion";
import { Search, Compass, Sparkles, TrendingUp } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const STEP_REVEAL = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};
const stepTransition = (i) => ({ duration: 0.55, delay: i * 0.08, ease: EASE });

const STEPS = [
  {
    n: "01",
    Icon: Search,
    title: "Discover",
    desc: "We embed with your team to audit the brand, audience, channels, and competitors — surfacing the real levers that move revenue.",
  },
  {
    n: "02",
    Icon: Compass,
    title: "Strategy",
    desc: "We translate insight into a written quarterly playbook: positioning, channel mix, content pillars, and the KPIs we'll be measured on.",
  },
  {
    n: "03",
    Icon: Sparkles,
    title: "Create",
    desc: "In-house creative, photo, and video teams ship scroll-stopping work on schedule — every asset built to the strategy, not to a template.",
  },
  {
    n: "04",
    Icon: TrendingUp,
    title: "Scale",
    desc: "We optimize weekly, double down on what's working, and reinvest proven creative into paid — compounding reach and pipeline month over month.",
  },
];

export default function Process() {
  return (
    <section
      id="process"
      className="relative bg-[#050B26] py-24 sm:py-32 overflow-hidden"
      data-testid="process-section"
    >
      {/* Subtle blueprint grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(91,128,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(91,128,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-16"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#5b80ff] font-semibold mb-4">
              How We Work
            </p>
            <h2 className="font-arial-black text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight max-w-2xl">
              A four-step engine.
              <span className="block text-white/45">Built to compound.</span>
            </h2>
          </div>
          <p className="text-white/65 max-w-md leading-relaxed">
            No mystery, no jargon. The same disciplined process every Knight
            Media Group client moves through — from kickoff to category leader.
          </p>
        </motion.div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {/* Animated connecting line with a sweeping beam */}
          <div
            className="hidden lg:block absolute top-[3.75rem] left-[12%] right-[12%] h-px overflow-visible z-30"
            aria-hidden="true"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#5b80ff]/35 to-transparent" />
            <motion.div
              className="absolute top-1/2 -translate-y-1/2 h-[3px] w-28 rounded-full bg-gradient-to-r from-transparent via-[#5b80ff] to-transparent blur-[1px]"
              initial={{ left: "-12%" }}
              whileInView={{ left: ["-12%", "100%"] }}
              viewport={{ once: false }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.6 }}
            />
            {[
              { left: "17%", label: "then" },
              { left: "50%", label: "leads to" },
              { left: "83%", label: "then" },
            ].map((c, ci) => (
              <motion.span
                key={ci}
                className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#5b80ff] font-semibold whitespace-nowrap bg-[#0a1130] px-2.5 py-1 rounded-full border border-[#1440E1]/35 shadow-[0_4px_14px_-4px_rgba(0,0,0,0.6)]"
                style={{ left: c.left }}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.9 + ci * 0.25, ease: "easeOut" }}
              >
                <motion.span
                  className="inline-block"
                  animate={{ x: [0, 3, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: ci * 0.3 }}
                >
                  {c.label} &rarr;
                </motion.span>
              </motion.span>
            ))}
          </div>

          {STEPS.map(({ n, Icon, title, desc }, i) => (
            <motion.article
              key={title}
              {...STEP_REVEAL}
              transition={stepTransition(i)}
              className="relative bg-white/[0.04] border border-white/10 backdrop-blur-sm p-7 sm:p-8 hover:bg-white/[0.07] hover:border-[#1440E1]/45 transition-colors group"
              data-testid={`process-step-${i}`}
            >
              <div className="relative size-12 rounded-full bg-[#1440E1] text-white flex items-center justify-center mb-7 shadow-[0_0_28px_rgba(20,64,225,0.45)] z-10">
                <motion.span
                  className="absolute inset-0 rounded-full border border-[#5b80ff]"
                  animate={{ scale: [1, 1.7], opacity: [0.6, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut", delay: i * 0.35 }}
                  aria-hidden="true"
                />
                <Icon size={20} strokeWidth={2.25} className="relative z-10 group-hover:scale-110 transition-transform" />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#5b80ff] mb-2">
                Step {n}
              </p>
              <h3 className="font-arial-bold text-2xl text-white leading-tight mb-3">
                {title}
              </h3>
              <p className="text-sm text-white/70 leading-relaxed">{desc}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
